import { FC, Fragment, useEffect, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  CheckIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

interface BonzahAcknowledgementProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const BONZAH_POLICY_LINKS = [
  {
    title: "Eligible & restricted vehicle types",
    short: "Eligible vehicles",
    subtitle: "Confirm the booked vehicle is covered by Bonzah",
    href: "https://bonzah.com/included-and-restricted-vehicle-types",
  },
  {
    title: "Bonzah Terms",
    short: "Terms",
    subtitle: "Terms governing the Bonzah insurance offering",
    href: "https://bonzah.com/terms",
  },
  {
    title: "Bonzah Privacy Policy",
    short: "Privacy Policy",
    subtitle: "How Bonzah collects and uses your data",
    href: "https://bonzah.com/privacy",
  },
];

const BonzahAcknowledgement: FC<BonzahAcknowledgementProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [agreed, setAgreed] = useState(false);
  // The disclosure opens at three lines so the whole pop-up, buttons
  // included, fits one phone screen; View more shows the rest and the
  // body scrolls.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAgreed(false);
      setExpanded(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!agreed) return;
    onConfirm();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <DialogPanel className="w-full max-w-[560px] max-h-[calc(100dvh-32px)] flex flex-col bg-bg-color border border-secondary-100 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex-shrink-0 border-b border-secondary-100 px-5 pt-5 pb-3 sm:px-6 sm:pt-6 sm:pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-lg font-bold text-text-color sm:text-xl">
                      Confirm Bonzah Insurance
                    </h2>
                    <button
                      type="button"
                      className="text-text-color opacity-70 hover:opacity-100"
                      onClick={onClose}
                      aria-label="Close"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-text-color opacity-70">
                    Please review and acknowledge that the vehicle you are
                    renting is eligible for Bonzah coverage, you have read and
                    agree to Bonzah&apos;s Terms, and to Bonzah&apos;s Privacy
                    Policy before selecting Bonzah Insurance.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="mx-5 mt-3 rounded-lg sm:mx-6 sm:mt-4 border border-secondary-100 bg-secondary-50/40 px-4 py-3 text-xs leading-relaxed text-text-color opacity-90">
                    <p className="mb-1 text-sm font-semibold opacity-100">
                      Insurance Disclosure
                    </p>
                    <p className={expanded ? undefined : "line-clamp-3"}>
                      By purchasing coverage through this site, you acknowledge
                      that Pablow Inc. dba{" "}
                      <a
                        href="https://bonzah.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        Bonzah.com
                      </a>{" "}
                      (&ldquo;Bonzah&rdquo;) is the licensed broker of record
                      and offers insurance coverage through various insurance
                      carriers. The specific carrier issuing your policy will
                      be identified at the time of purchase and in your policy
                      documents.
                    </p>
                    {expanded && (
                      <>
                        <p className="mt-2">
                          Coverage may include Collision Damage Waiver (CDW/LDW),
                          Rental Car Liability Insurance (RCLI), and Supplemental
                          Liability Insurance (SLI) as offered. Coverage excludes
                          medical payments, Personal Injury Protection (PIP),
                          Underinsured Motorist (UIM), and Uninsured Motorist (UM)
                          coverage where permitted by law. Full terms, conditions,
                          limits, and exclusions are set forth in the policy
                          documents provided at the time of purchase.
                        </p>
                        <p className="mt-2">
                          By proceeding with your purchase, you agree to
                          Bonzah.com&apos;s Terms of Service and Privacy Policy.
                        </p>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      aria-expanded={expanded}
                      className="mt-1.5 text-sm font-semibold text-primary-brand hover:underline"
                    >
                      {expanded ? "View less" : "View more"}
                    </button>
                  </div>

                  <div className="space-y-2 px-5 pt-3 pb-1 sm:px-6 sm:pt-4 sm:pb-2">
                    {BONZAH_POLICY_LINKS.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-3 rounded-lg border border-secondary-100 px-4 py-2.5 transition hover:bg-secondary-50 sm:items-start sm:py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-text-color">
                            {link.title}
                          </div>
                          <div className="mt-0.5 hidden text-xs text-text-color opacity-70 sm:block">
                            {link.subtitle}
                          </div>
                        </div>
                        <ArrowTopRightOnSquareIcon className="h-5 w-5 sm:mt-0.5 shrink-0 text-text-color opacity-70" />
                      </a>
                    ))}
                  </div>

                  {/* 1NOW-1173: use the Headless UI Checkbox + CheckIcon
                      pattern the rest of the app uses. The previous native
                      <input type="checkbox"> rendered white-on-white under
                      @tailwindcss/forms (only a faint secondary-100 border),
                      so it was effectively invisible and renters couldn't
                      enable "I Agree & Continue". */}
                  <div className="flex items-start gap-3 px-5 py-3 sm:px-6 sm:py-4">
                    <Checkbox
                      checked={agreed}
                      onChange={setAgreed}
                      className="group mt-0.5 size-6 flex-shrink-0 cursor-pointer rounded-md p-1 ring-1 ring-inset ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20"
                    >
                      <CheckIcon
                        className={`size-4 stroke-primary-brand stroke-2 ${
                          agreed ? "visible" : "invisible"
                        }`}
                      />
                    </Checkbox>
                    <span
                      className="cursor-pointer select-none text-sm text-text-color"
                      onClick={() => setAgreed((prev) => !prev)}
                    >
                      I have reviewed the eligible vehicle types, Terms, and
                      Privacy Policy and confirm I agree.
                    </span>
                  </div>
                </div>

                <div className="flex flex-shrink-0 justify-end gap-3 border-t border-secondary-100 px-5 py-3 sm:px-6 sm:py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-secondary-100 px-4 py-2 text-sm font-medium text-text-color hover:bg-secondary-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!agreed}
                    className="rounded-md bg-primary-brand px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    I Agree & Continue
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BonzahAcknowledgement;
